import { useState } from "react";
import { usePortal, type InviteMemberBody, type MemberRow, type MembershipRole } from "@app/portal-sdk";
import { Alert, Button, Stack, TextField } from "@app/ui-kit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import { errorMessage } from "../lib/useApiQuery";

type Props = {
  open: boolean;
  clientId: string;
  clientName: string;
  onClose: () => void;
  onInvited: (member: MemberRow) => void;
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const InviteDialog = ({ open, clientId, clientName, onClose, onInvited }: Props) => {
  const { api } = usePortal();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<MembershipRole>("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEmail("");
    setName("");
    setRole("member");
    setError(null);
  };

  const submit = async () => {
    if (!EMAIL.test(email.trim())) {
      setError("Enter a valid email address");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: InviteMemberBody = { email: email.trim(), name: name.trim() || undefined, role };
      const row = await api.request<MemberRow>(`/admin/clients/${clientId}/invitations`, { method: "POST", json: body });
      onInvited(row);
      reset();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Invite to {clientName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoFocus
            fullWidth
          />
          <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} fullWidth />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(event) => setRole(event.target.value as MembershipRole)}
            helperText={role === "client_admin" ? "Can upload documents and see the team." : "Can use the portal and its applications."}
            fullWidth
          >
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="client_admin">Client admin</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="text" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={busy}>
          Send invitation
        </Button>
      </DialogActions>
    </Dialog>
  );
};
